import { FLEX_DEMO_STEPS, flexDemoSessionStorageKey, type FlexDemoStep } from "./flexDemo.const";

export type FlexDemoSession = {
  step: FlexDemoStep;
  ndaAccepted: boolean;
  selectedProposalId: string | null;
  answers: Record<string, string>;
  welcomeDismissed: boolean;
  collaborationsNavOpened: boolean;
  workspaceOpened: boolean;
};

const DEFAULT_SESSION: FlexDemoSession = {
  step: "explore",
  ndaAccepted: false,
  selectedProposalId: null,
  answers: {},
  welcomeDismissed: false,
  collaborationsNavOpened: false,
  workspaceOpened: false,
};

function isStep(value: unknown): value is FlexDemoStep {
  return typeof value === "string" && (FLEX_DEMO_STEPS as readonly string[]).includes(value);
}

export function loadFlexDemoSession(userId: string): FlexDemoSession {
  try {
    const raw = sessionStorage.getItem(flexDemoSessionStorageKey(userId));
    if (!raw) return { ...DEFAULT_SESSION };
    const parsed = JSON.parse(raw) as Partial<FlexDemoSession>;
    return {
      step: isStep(parsed.step) ? parsed.step : "explore",
      ndaAccepted: Boolean(parsed.ndaAccepted),
      selectedProposalId: typeof parsed.selectedProposalId === "string" ? parsed.selectedProposalId : null,
      answers: parsed.answers && typeof parsed.answers === "object" ? parsed.answers : {},
      welcomeDismissed: Boolean(parsed.welcomeDismissed),
      collaborationsNavOpened: Boolean(parsed.collaborationsNavOpened),
      workspaceOpened: Boolean(parsed.workspaceOpened),
    };
  } catch {
    return { ...DEFAULT_SESSION };
  }
}

export function saveFlexDemoSession(userId: string, session: FlexDemoSession): void {
  try {
    sessionStorage.setItem(flexDemoSessionStorageKey(userId), JSON.stringify(session));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearFlexDemoSession(userId: string): void {
  try {
    sessionStorage.removeItem(flexDemoSessionStorageKey(userId));
  } catch {
    /* ignore */
  }
}

export function nextFlexDemoStep(step: FlexDemoStep): FlexDemoStep {
  const index = FLEX_DEMO_STEPS.indexOf(step);
  return FLEX_DEMO_STEPS[Math.min(index + 1, FLEX_DEMO_STEPS.length - 1)];
}

export function prevFlexDemoStep(step: FlexDemoStep): FlexDemoStep {
  const index = FLEX_DEMO_STEPS.indexOf(step);
  return FLEX_DEMO_STEPS[Math.max(index - 1, 0)];
}
